import { Injectable, signal } from "@angular/core"
import { HDBreaker, HDButton, HDButtonList, HDCardText, HDDivider, HDIcon, HDImage, HDImageWithTitle, HDpdf, HDText, HotelDirectory, Image_1_2, Image_2_1, ImageCarousel, Layouts, Page, SoMe } from "@api/communication/models/hotel-directory"
import { HDComponent } from "./hotel-directory-edit/hotel-directory-edit-list"
import { BehaviorSubject, Observable, Subject } from "rxjs"
import Quill from "quill"
import { MediaFile } from "@api/utility/models/media-library"
import { DynamicVariableList } from "@api/communication/models/communication"
import { v4 as uuidv4 } from "uuid"
import { sha256 } from "js-sha256"
import { BetaFeatures } from "@api/utility/models/BetaFeatures"
import { StateService } from "@shared/services/state.service"

export interface quillList {
  Quill: Quill
  Id: string
  SlotKey?: string
  Lang?: string
}

export class RangeStatic {
  index: number
  length: number
}

export class DVMap {
  elementid: string
  placeholder: string
}

@Injectable({
  providedIn: "root",
})
export class HotelDirectoryService {
  public mediaLibraryList: MediaFile[] = []
  private hd: HotelDirectory
  private PreviewElements: HDComponent[] = []
  private subPageList: Page[] = []
  // Selected layout/section
  private selectedElement: BehaviorSubject<HDComponent> = new BehaviorSubject<HDComponent>({ imageUrl: "", title: "", backendName: "", category: "", data: null, id: "", priority: 0 })
  selectedElementChanged: Observable<HDComponent> = this.selectedElement.asObservable()
  private selectedSectionIndex: BehaviorSubject<number> = new BehaviorSubject<number>(-1)
  selectedSectionChanged: Observable<number> = this.selectedSectionIndex.asObservable()
  private selectedLanguage: BehaviorSubject<string> = new BehaviorSubject<string>("en")
  selectedLanguageChanged: Observable<string> = this.selectedLanguage.asObservable()
  private defaultLanguage: string
  private hiddenContent = new Map<object | string, Map<string, unknown>>()
  //Transfer info between sidebar and preview component
  private currentTextSelection: RangeStatic
  private previousTextSelection: RangeStatic
  private selectedImageElement: BehaviorSubject<HDImage | Image_1_2 | Image_2_1 | ImageCarousel | null> = new BehaviorSubject<HDImage | Image_1_2 | Image_2_1 | ImageCarousel | null>(null)
  changedImageElement: Observable<HDImage | Image_1_2 | Image_2_1 | ImageCarousel | null> = this.selectedImageElement.asObservable()
  private selectedTextElement: HDText
  private selectedBreakerElement: BehaviorSubject<HDBreaker> = new BehaviorSubject<HDBreaker>(null)
  changedBreakerElement: Observable<HDBreaker> = this.selectedBreakerElement.asObservable()
  private selectedCardElement: HDImage | HDCardText | null
  private selectedCardLanguage: string
  private selectedButtonElement: HDButton | HDButtonList
  private selectedButtonLanguage: string
  private selectedSoMeElement: BehaviorSubject<SoMe> = new BehaviorSubject<SoMe>(null)
  changedSoMeElement: Observable<SoMe> = this.selectedSoMeElement.asObservable()
  private selectedPdfElement: BehaviorSubject<HDpdf> = new BehaviorSubject<HDpdf>(null)
  changedPdfElement: Observable<HDpdf> = this.selectedPdfElement.asObservable()
  //Highlighting element in section
  private highlighedElement: string
  public focusButtonText = false
  private highlightedLanguage: string = ""
  readonly translationMode = signal(false)
  private sideBarMode: string = "Insert"
  private activeSectionId: string | null = null
  private activeCardId: string | null = null
  private hotelColor: string = ""

  private selectedInnerElement: BehaviorSubject<string> = new BehaviorSubject<string>("")
  changedInnerElement: Observable<string> = this.selectedInnerElement.asObservable()

  //Dynamic variables
  public dynamicVariableList: DynamicVariableList[] = []
  private dvListChange: Subject<DVMap> = new Subject<DVMap>()
  dvListChanged: Observable<DVMap> = this.dvListChange.asObservable()
  readonly betaFeatures = signal<BetaFeatures | null>(null)
  public currentPage: Page

  private readonly viewOnlyMode = signal(false)

  quillEditorList: quillList[] = []

  private recentlyClosedHotelDirectoryId: string = null
  private listScrollTop: number = 0

  private adjustingImageSlot: string | null = null
  private adjustingDimensions: { width: number; height: number } | null = null
  private adjustClickedOutside: boolean = false
  private adjustClickedOutsidePulse: number = 0
  private adjustClickedOutsideTimeout: ReturnType<typeof setTimeout> | null = null
  private adjustMeasureRequest = new Subject<string>()
  adjustMeasureRequested: Observable<string> = this.adjustMeasureRequest.asObservable()

  private carouselSlideIndex = new Subject<{ elementId: string; index: number }>()
  carouselSlideIndexChanged: Observable<{ elementId: string; index: number }> = this.carouselSlideIndex.asObservable()

  readonly focusedTextRole = signal<"header" | "paragraph" | null>(null)

  public setFocusedTextRole(role: "header" | "paragraph" | null): void {
    this.focusedTextRole.set(role)
  }

  readonly translationLocked = signal(false)
  readonly translationLockedPulse = signal(0)
  private translationLockedTimeout: ReturnType<typeof setTimeout> | null = null

  private activeHotelId: string = null

  constructor(private stateService: StateService) {
    this.activeHotelId = this.stateService.selectedHotel?.Id
    this.stateService.selectedHotelObservable.subscribe((hotel) => {
      if (!hotel?.Id || hotel.Id === this.activeHotelId) return
      this.activeHotelId = hotel.Id
      this.resetHotelScopedState()
    })
  }

  public triggerTranslationLockedToaster(): void {
    this.translationLocked.set(true)
    this.translationLockedPulse.update((p) => p + 1)
    if (this.translationLockedTimeout) clearTimeout(this.translationLockedTimeout)
    this.translationLockedTimeout = setTimeout(() => {
      this.translationLocked.set(false)
      this.translationLockedTimeout = null
    }, 8000)
  }

  public dismissTranslationLockedToaster(): void {
    this.translationLocked.set(false)
    if (this.translationLockedTimeout) {
      clearTimeout(this.translationLockedTimeout)
      this.translationLockedTimeout = null
    }
  }

  public setRecentlyClosedHotelDirectoryId(id: string) {
    this.recentlyClosedHotelDirectoryId = id
  }
  public getRecentlyClosedHotelDirectoryId(): string {
    return this.recentlyClosedHotelDirectoryId
  }

  public setListScrollTop(value: number) {
    this.listScrollTop = value
  }
  public getListScrollTop(): number {
    return this.listScrollTop
  }

  public getAdjustingImageSlot(): string | null {
    return this.adjustingImageSlot
  }

  public setAdjustingImageSlot(slot: string | null): void {
    this.adjustingImageSlot = slot
  }

  public getAdjustingDimensions(): { width: number; height: number } | null {
    return this.adjustingDimensions
  }

  public setAdjustingDimensions(dims: { width: number; height: number } | null): void {
    this.adjustingDimensions = dims
  }

  public requestAdjustMeasure(slot: string): void {
    this.adjustMeasureRequest.next(slot)
  }

  public getAdjustClickedOutside(): boolean {
    return this.adjustClickedOutside
  }

  public setAdjustClickedOutside(val: boolean): void {
    this.adjustClickedOutside = val
  }

  public getAdjustClickedOutsidePulse(): number {
    return this.adjustClickedOutsidePulse
  }

  public bumpAdjustClickedOutsidePulse(): void {
    this.adjustClickedOutsidePulse++
  }

  public guardAgainstAdjusting(): boolean {
    if (!this.adjustingImageSlot) return false
    this.adjustClickedOutside = true
    this.adjustClickedOutsidePulse++
    if (this.adjustClickedOutsideTimeout) clearTimeout(this.adjustClickedOutsideTimeout)
    this.adjustClickedOutsideTimeout = setTimeout(() => {
      this.adjustClickedOutside = false
      this.adjustClickedOutsideTimeout = null
    }, 3000)
    return true
  }

  public setBetaFeatures(features: BetaFeatures) {
    this.betaFeatures.set(features)
  }
  public getBetaFeatures(): BetaFeatures | null {
    return this.betaFeatures()
  }

  public setCurrentPage(page: Page) {
    this.currentPage = page
  }
  public getCurrentPage(): Page {
    return this.currentPage
  }

  public setPreviewElements(elements: HDComponent[]) {
    this.PreviewElements = elements
  }
  public getPreviewElements(): HDComponent[] {
    return this.PreviewElements
  }

  public setHotelColor(color: string) {
    this.hotelColor = color
  }
  public getHotelColor(): string {
    return this.hotelColor
  }

  public setSideBarMode(mode: string) {
    this.sideBarMode = mode
  }
  public getSideBarMode(): string {
    return this.sideBarMode
  }
  public setActiveSectionId(id: string | null): void {
    this.activeSectionId = id
  }
  public getActiveSectionId(): string | null {
    return this.activeSectionId
  }
  public setActiveCardId(id: string | null): void {
    this.activeCardId = id
  }
  public getActiveCardId(): string | null {
    return this.activeCardId
  }

  public isSectionActive(sectionId: string | null): boolean {
    return this.activeSectionId === (sectionId ?? null)
  }
  public isCardActive(cardId: string | null): boolean {
    return this.activeCardId === (cardId ?? null) && this.activeCardId !== null
  }
  public isElementSelected(id: string | null): boolean {
    return !!id && this.getSelectedElement()?.id === id
  }

  public descendToSection(section: HDComponent): void {
    this.setActiveSectionId(section.id ?? null)
    this.setActiveCardId(null)
    this.setHighlightedElement("")
    this.setHighlightedLanguage("")
    this.setInnerElement("")
    this.setSelectedElement(section)
  }
  public descendToChild(child: HDComponent): void {
    this.setActiveCardId(null)
    this.setHighlightedElement("")
    this.setHighlightedLanguage("")
    this.setInnerElement("")
    this.setSelectedElement(child)
  }
  public descendToCard(card: HDComponent): void {
    this.setActiveCardId(card.id ?? null)
    this.setHighlightedElement("")
    this.setHighlightedLanguage("")
    this.setInnerElement("")
    this.setSelectedElement(card)
  }

  public getSectionContaining(elementId: string | null): HDComponent | null {
    if (!elementId) return null
    return this.PreviewElements.find((el) => el.category === "Section" && el.data?.some((d) => d.id === elementId)) ?? null
  }

  public selectAtFullDepth(element: HDComponent): void {
    const category = element.category || this.getCategoryByObjType(element.backendName)
    const parentSection = this.getSectionContaining(element.id ?? null)
    this.setActiveSectionId(parentSection?.id ?? null)
    this.setActiveCardId(category === "Card" ? (element.id ?? null) : null)
    this.setHighlightedElement("")
    this.setHighlightedLanguage("")
    this.setInnerElement("")
    this.setElementBasedOnElementType(element)
    this.setSelectedElement(element)
  }

  public setInnerElement(element: string) {
    this.selectedInnerElement.next(element)
  }
  public getInnerElement(): string {
    return this.selectedInnerElement.value
  }

  public setDvListChange(map: DVMap) {
    this.dvListChange.next(map)
  }

  public setTextSelection(range: RangeStatic) {
    this.previousTextSelection = this.currentTextSelection
    this.currentTextSelection = range
  }
  public getTextSelection(): RangeStatic {
    return this.currentTextSelection
  }
  public getPreviousTextSelection(): RangeStatic {
    return this.previousTextSelection
  }

  public getDynamicVariableList(): DynamicVariableList[] {
    return this.dynamicVariableList
  }
  public setDynamicVariableList(list: DynamicVariableList[]) {
    this.dynamicVariableList = list
  }
  public addToDynamicVariableList(element: DynamicVariableList) {
    this.dynamicVariableList.push(element)
  }

  public setCardLanguage(lang: string) {
    this.selectedCardLanguage = lang
  }
  public getCardLanguage(): string {
    return this.selectedCardLanguage
  }

  public setButtonLanguage(lang: string) {
    this.selectedButtonLanguage = lang
  }
  public getButtonLanguage(): string {
    return this.selectedButtonLanguage
  }

  public setDefaultLanguage(lang: string) {
    this.defaultLanguage = lang
  }
  public getDefaultLang(): string {
    return this.defaultLanguage
  }

  public stashHiddenContent(owner: object | string, field: string, value: unknown): void {
    if (!this.hiddenContent.has(owner)) this.hiddenContent.set(owner, new Map<string, unknown>())
    this.hiddenContent.get(owner).set(field, value)
  }
  public takeHiddenContent<T>(owner: object | string, field: string): T | undefined {
    const stash = this.hiddenContent.get(owner)
    const value = stash?.get(field) as T | undefined
    stash?.delete(field)
    return value
  }

  public setTranslationMode(mode: boolean): void {
    this.translationMode.set(mode)
  }
  public getTranslationMode(): boolean {
    return this.translationMode()
  }

  public isDefaultLanguageCopy(langUsed: string): boolean {
    if (!this.translationMode()) return false
    if (this.getLanguage() === this.getDefaultLang()) return false
    if (!langUsed) return true
    return langUsed === this.getDefaultLang()
  }

  public setSubPageList(pageList: Page[]) {
    this.subPageList = pageList
  }
  public getSubPageList(): Page[] {
    return this.subPageList
  }

  public setHotelDirectory(value: HotelDirectory) {
    this.hd = value
  }
  getHotelDirectory() {
    return this.hd
  }
  public resetTemplate() {
    this.hd = null
  }
  public setMediaLibraryList(list: MediaFile[]): void {
    this.mediaLibraryList = list
  }
  public getMediaLibraryList(): MediaFile[] {
    return this.mediaLibraryList
  }

  public setHighlightedElement(newElement: string): void {
    this.highlighedElement = newElement
  }
  public getHighlightedElement(): string {
    return this.highlighedElement
  }

  public setHighlightedLanguage(lang: string): void {
    this.highlightedLanguage = lang
  }
  public getHighlightedLanguage(): string {
    return this.highlightedLanguage
  }

  public setCarouselSlideIndex(elementId: string, index: number): void {
    this.carouselSlideIndex.next({ elementId, index })
  }

  public setSelectedElement(element: HDComponent) {
    this.selectedElement.next(element)
  }
  public getSelectedElement(): HDComponent {
    return this.selectedElement?.value
  }
  public setSelectedSection(index: number) {
    this.selectedSectionIndex.next(index)
  }
  public getSelectedSectionIndex(): number {
    return this.selectedSectionIndex?.value
  }
  public setLanguage(lang: string) {
    this.selectedLanguage.next(lang)
  }
  public getLanguage(): string {
    return this.selectedLanguage.value
  }
  public getQuillEditor() {
    return this.quillEditorList
  }
  public addQuillEditor(quill: Quill, id: string, slotKey?: string, lang?: string): void {
    this.quillEditorList.push({ Id: id, Quill: quill, SlotKey: slotKey, Lang: lang })
  }

  public removeQuillEditor(quill: Quill): void {
    this.quillEditorList = this.quillEditorList.filter((x) => x.Quill !== quill)
  }

  public getQuillEditorBySlot(id: string, slotKey: string, lang?: string): Quill | null {
    return this.quillEditorList.find((x) => x.Id === id && x.SlotKey === slotKey && (!lang || x.Lang === lang))?.Quill ?? null
  }

  public snapIndexOutOfTag(quill: Quill, index: number): number {
    const text = quill.getText()
    const regex = /\[(.*?)\]/g
    let match: RegExpExecArray | null
    while ((match = regex.exec(text)) !== null) {
      const start = match.index
      const end = start + match[0].length
      if (index <= start || index >= end) continue
      if (!this.dynamicVariableList.some((x) => x.placeholder === match[0])) continue
      return index - start <= end - index ? start : end
    }
    return index
  }

  public mapToJsonDictionary(m): any {
    return Array.from(m).reduce((obj, [key, value]) => {
      obj[key] = value
      return obj
    }, {})
  }

  public convertAllMapsToJson(input: any): any {
    if (input instanceof Date) {
      return input
    } else if (input instanceof Map) {
      return Array.from(input).reduce((obj, [key, value]) => {
        obj[key] = this.convertAllMapsToJson(value) // Recursively convert nested Maps
        return obj
      }, {})
    } else if (Array.isArray(input)) {
      return input.map((item) => this.convertAllMapsToJson(item)) // Handle arrays
    } else if (input !== null && typeof input === "object") {
      return Object.keys(input).reduce((obj, key) => {
        obj[key] = this.convertAllMapsToJson(input[key]) // Recursively process object properties
        return obj
      }, {})
    } else {
      return input // Return the value if it's neither a Map, Array, nor object
    }
  }

  public setBreakerElement(breakerElement: HDBreaker) {
    this.selectedBreakerElement.next(breakerElement)
  }
  public getBreakerElement() {
    return this.selectedBreakerElement.value
  }

  public setTextElement(textElement: HDText) {
    this.selectedTextElement = textElement
  }
  public getTextElement() {
    return this.selectedTextElement
  }

  public setButtonElement(buttonElement: HDButton | HDButtonList) {
    this.selectedButtonElement = buttonElement
  }
  public getButtonElement() {
    return this.selectedButtonElement
  }

  public setSoMeElement(someElement: SoMe) {
    this.selectedSoMeElement.next(someElement)
  }

  public getSoMeElement(): SoMe {
    return this.selectedSoMeElement?.value
  }

  public setPdfElement(pdfElement: HDpdf) {
    this.selectedPdfElement.next(pdfElement)
  }

  public getPdfElement() {
    return this.selectedPdfElement.value
  }
  public getCardElement(): HDCardText | HDImage | null {
    return this.selectedCardElement
  }

  public setCardElement(cardElement: HDCardText | HDImage | null) {
    this.selectedCardElement = cardElement
  }
  public setImageElement(imageElement: HDImage | Image_1_2 | Image_2_1 | ImageCarousel | null) {
    this.selectedImageElement.next(imageElement)
  }
  public getImageElement() {
    return this.selectedImageElement.value
  }

  public mediaNameFromUrl(imgUrl: string): string {
    const regex = /\/([a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12})\./
    var url = imgUrl
    const match = url?.match(regex)
    if (match) {
      return this.mediaLibraryList.find((x) => x.id === match[1])?.name
    } else {
      return ""
    }
  }
  public mediaLibFromUrl(imgUrl: string): MediaFile {
    const regex = /\/([a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12})\./
    var url = imgUrl
    const match = url.match(regex)
    if (match) {
      return this.mediaLibraryList.find((x) => x.id === match[1])
    } else {
      return null
    }
  }

  public imgUrlByObjType(objType: string): string {
    switch (objType) {
      case "Header":
      case "Subheader":
      case "Paragraph":
        return "https://aeroguestblobstorageprod.blob.core.windows.net/icons23/Other/Other/text.svg"
      case "Button":
      case "Button_list":
        return "https://aeroguestblobstorageprod.blob.core.windows.net/icons23/Other/Other/button.svg"
      case "Image_1":
        return "https://aeroguestblobstorageprod.blob.core.windows.net/icons23/Other/Other/image_1.svg"
      case "Image_2/1":
        return "https://aeroguestblobstorageprod.blob.core.windows.net/icons23/Other/Other/image_2-1.svg"
      case "Image_1/2":
        return "https://aeroguestblobstorageprod.blob.core.windows.net/icons23/Other/Other/image_1-2.svg"
      case "Carousel":
        return "https://aeroguestblobstorageprod.blob.core.windows.net/icons23/Other/Other/carousel.svg"
      case "Card_vertical_large_right":
      case "Card_vertical_large_left":
      case "Card_vertical_medium_right":
      case "Card_vertical_medium_left":
      case "Card_horizontal_large":
      case "Card_horizontal_medium":
      case "Card_horizontal_small":
        return "https://aeroguestblobstorageprod.blob.core.windows.net/icons23/Other/Other/card_image_right.svg"
      case "Divider":
        return "https://aeroguestblobstorageprod.blob.core.windows.net/icons23/Other/Other/divider.svg"
      case "Spacer":
        return "https://aeroguestblobstorageprod.blob.core.windows.net/icons23/Other/Other/spacer.svg"
      case "SoMe":
        return "https://aeroguestblobstorageprod.blob.core.windows.net/icons23/Other/Other/some.svg"
      case "Mandatory_section":
        return "https://aeroguestblobstorageprod.blob.core.windows.net/icons23/Other/Other/mandatory.svg"
      case "Booking_card":
        return "https://aeroguestblobstorageprod.blob.core.windows.net/icons23/Other/Other/booking_card.svg"
      case "PDF":
        return "https://aeroguestblobstorageprod.blob.core.windows.net/icons23/Other/Other/pdf.svg"
      default:
        return ""
    }
  }

  public getCategoryByObjType(objType: string): string {
    switch (objType) {
      case "Header":
      case "Subheader":
      case "Paragraph":
        return "Text"
      case "Button":
      case "Button_list":
        return "Button"
      case "Image_1":
      case "Image_2/1":
      case "Image_1/2":
      case "Carousel":
        return "Image"
      case "Card_vertical_large_right":
      case "Card_vertical_large_left":
      case "Card_vertical_medium_right":
      case "Card_vertical_medium_left":
      case "Card_horizontal_large":
      case "Card_horizontal_medium":
      case "Card_horizontal_small":
        return "Card"
      case "Divider":
      case "Spacer":
        return "Breaker"
      case "SoMe":
        return "SoMe"
      case "Mandatory_section":
        return "mandatory"
      case "Booking_card":
        return "Booking_card"
      case "PDF":
        return "PDF"
      case "HDSection":
        return "Section"
    }
  }
  public placeHolderToDynamicVariable(text: string): string {
    let textchange = text
    const regex = /\[(.*?)\]/g
    const matches = textchange.match(regex)
    if (matches === null) return text
    const textsWithoutBrackets = matches.map((match) => match.substring(1, match.length - 1))
    textsWithoutBrackets.forEach((placeholder) => {
      var funcId = this.dynamicVariableList.find((x) => x.placeholder === `[${placeholder}]`)?.functionId
      textchange = textchange.replaceAll(`[${placeholder}]`, `{(${placeholder}):(${funcId})}`)
    })
    return textchange
  }
  public dynamicVariableToPlaceholder(text: string): string {
    let textChange = text
    const regex = /{\(([^)]+?)\):(\()[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(\))}/g
    const guidregex = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g
    const regexPlaceholderName = /\(([^,]+),([^)]+)\)/
    const regexSingleName = /(?<=\()[^)]*(?=\))/
    const dynamicVariableListMatch = textChange.match(regex)
    if (dynamicVariableListMatch === null) return text
    var usedList: string[] = []
    dynamicVariableListMatch.forEach((dynamicVariable, index) => {
      if (usedList.includes(dynamicVariable)) {
        return
      }
      let funcIdMatch = dynamicVariable?.match(guidregex)[0]
      var placeholderName = ""
      if (dynamicVariable.includes(",")) {
        placeholderName = regexPlaceholderName.exec(dynamicVariable)[0]
      } else {
        placeholderName = regexSingleName.exec(dynamicVariable)[0]
      }
      var placeholder = `[${placeholderName}]`
      if (this.dynamicVariableList.findIndex((x) => x.functionId === funcIdMatch) === -1) {
        this.dynamicVariableList.push({ placeholder: placeholder, functionId: funcIdMatch })
      }
      textChange = textChange.replaceAll(dynamicVariable, `[${placeholderName}]`)
      usedList.push(dynamicVariable)
    })
    return textChange
  }

  public getLinkPlaceholder(context: string): string {
    switch (context) {
      case "Web Address":
        return "https://example.com"
      case "Phone Number":
        return "e.g. +141555551212"
      case "Email Address":
        return "example@email.com"
      default:
        return "Type here"
    }
  }

  public normalizeWebAddress(link: string): string {
    const withoutProtocol = link.trim().replace(/^(https?:\/*)+/i, "")
    return withoutProtocol ? `https://${withoutProtocol}` : ""
  }

  public checkIfSameContext(context: string): boolean {
    if (this.highlighedElement !== context) return true
    if (this.highlighedElement === context) return false
    if (this.highlighedElement === "" || this.highlighedElement) return true

    return false
  }

  // Every field below is scoped to one hotel. This service is providedIn root, so it
  // survives route changes and must be cleared explicitly when the hotel changes.
  public resetHotelScopedState(): void {
    this.hd = null
    this.mediaLibraryList = []
    this.PreviewElements = []
    this.subPageList = []
    this.currentPage = null
    this.dynamicVariableList = []
    this.hotelColor = ""
    this.recentlyClosedHotelDirectoryId = null
    this.resetEverything()
  }

  public resetEverything() {
    this.highlighedElement = ""
    this.hiddenContent.clear()
    this.selectedElement.next(null)
    this.selectedImageElement.next(null)
    this.selectedCardElement = null
    this.selectedCardLanguage = null
    this.selectedButtonLanguage = null
    this.selectedButtonElement = null
    this.selectedBreakerElement.next(null)
    this.selectedSoMeElement.next(null)
    this.selectedPdfElement.next(null)
    this.defaultLanguage = null
    this.translationMode.set(false)
    this.highlightedLanguage = ""
    this.setLanguage("")
    this.adjustingImageSlot = null
  }

  public setElementBasedOnElementType(element: HDComponent): void {
    if (!element || !element.data) {
      return
    }
    switch (element.backendName) {
      case "Button":
        this.selectedButtonElement = element.data[0] as HDButton
        break
      case "Button_list":
        this.selectedButtonElement = element.data[0] as HDButtonList
        this.setButtonElement(element.data[0] as HDButtonList)
        break
      case "Image_1":
        this.setImageElement(element.data[0] as HDImage)
        break
      case "Image_2/1": {
        const image2_1 = new Image_2_1()
        image2_1.Image1 = element.data[0] as HDImage
        image2_1.Image2 = element.data[1] as HDImage
        image2_1.Image3 = element.data[2] as HDImage
        this.setImageElement(image2_1)
        break
      }
      case "Image_1/2": {
        const image_1_2 = new Image_1_2()
        image_1_2.Image1 = element.data[0] as HDImage
        image_1_2.Image2 = element.data[1] as HDImage
        image_1_2.Image3 = element.data[2] as HDImage
        this.setImageElement(image_1_2)
        break
      }
      case "Carousel": {
        const carousel = new ImageCarousel()
        carousel.id = element.id
        carousel.type = "Carousel"
        carousel.ImageList = []
        element.data.forEach((img: HDImageWithTitle) => {
          carousel.ImageList.push(img)
        })
        this.setImageElement(carousel)
        break
      }
      case "Spacer": {
        var breaker = new HDBreaker()
        breaker = element.data[0] as HDBreaker
        this.setBreakerElement(breaker)
        break
      }
      case "SoMe": {
        var soMe = new SoMe()
        soMe.header = element.data[0] as HDText
        soMe.iconList = []
        element.data.forEach((soMeItem, index) => {
          if (index !== 0) {
            soMe.iconList.push(soMeItem as HDIcon)
          }
        })
        soMe.id = element.id
        this.setSoMeElement(soMe)
        break
      }
      case "Card_vertical_large_right":
      case "Card_vertical_large_left":
      case "Card_vertical_medium_right":
      case "Card_vertical_medium_left":
      case "Card_horizontal_large":
      case "Card_horizontal_medium":
      case "Card_horizontal_small": {
        if (this.selectedCardElement) {
          element.data.forEach((cardItem) => {
            if (cardItem.objType === this.selectedCardElement.objType && cardItem.priority === this.selectedCardElement.priority) {
              this.setCardElement(cardItem)
            }
          })
        }
      }
    }
  }
  public setViewOnlyMode(mode: boolean) {
    this.viewOnlyMode.set(mode)
  }
  public getViewOnlyMode(): boolean {
    return this.viewOnlyMode()
  }

  public defaultDataFlow(backendName: string): Layouts[] {
    if (backendName === "Divider") {
      return [
        {
          objType: "HDDivider",
          priority: 0,
        },
      ]
    }
    if (backendName === "Carousel") {
      return Array.from({ length: 3 }, (_, index) => this.emptyCarouselCard(index) as unknown as Layouts)
    }
    if (backendName === "Button_list") {
      return [this.emptyButtonList() as unknown as Layouts]
    }

    return []
  }

  public emptyButtonList(): HDButtonList {
    return {
      buttons: [
        {
          link: "",
          linkType: "Web Address",
          objType: "HDButton",
          priority: 0,
          style: "primary",
          text: { [this.getDefaultLang()]: "Button" },
          buttonImage: "https://aeroguestblobstorageprod.blob.core.windows.net/icons23/Navigation/plus.svg",
        },
      ],
      priority: 0,
      objType: "HDButtonList",
    }
  }

  public emptyCarouselCard(priority: number): HDImageWithTitle {
    return {
      title: new Map<string, string>([[this.getDefaultLang(), "Edit image title"]]),
      imageLink: "",
      imageCropping: "",
      linkType: "",
      link: "",
      priority,
      objType: "HDImageWithTitle",
    }
  }

  public convertObjectToLayoutFormat(obj: any): any {
    var objCopy = this.convertAllMapsToJson(obj)
    if (objCopy instanceof Array) {
      var layoutItems = []

      objCopy.forEach((element, index) => {
        var layoutItem = {
          priority: index,
          objType: element.backendName,
          layouts: element.data,
        }
        layoutItems.push(layoutItem)
      })
      return layoutItems
    }
  }

  public convertLayoutToData(section: Layouts): any {
    if (section.objType === "HDSection") {
      var sectionData = []
      section.layouts.forEach((layout) => {
        var layoutData = {
          backendName: layout.objType,
          data: layout.layouts,
          priority: layout.priority,
          category: this.getCategoryByObjType(layout.objType),
          id: layout?.id ?? uuidv4(),
        }
        sectionData.push(layoutData)
      })
      return sectionData
    }

    return section.layouts
  }

  public isHDCardText(subelement: Layouts): boolean {
    return subelement && subelement.objType === "HDCardText"
  }

  public isHDCardImage(subelement: Layouts): boolean {
    return subelement && subelement.objType === "HDImage"
  }
  public isButtonList(subelement: Layouts): boolean {
    return subelement && subelement.objType === "HDButtonList"
  }

  public getButtonList(subelement: Layouts): HDButtonList {
    if (this.isButtonList(subelement)) {
      return subelement as HDButtonList
    }
    return null
  }

  public getCardTextContent(subelement: Layouts): HDCardText {
    if (this.isHDCardText(subelement)) {
      return subelement
    }
    return null
  }
  public resizeCardData(layouts: any[], targetLength: number, backendName: string): Layouts[] {
    const images = layouts.filter((l) => l.objType === "HDImage")
    const texts = layouts.filter((l) => l.objType === "HDCardText")
    const newData: Layouts[] = []

    const isRight = backendName.includes("right")
    const isHorizontal = backendName.includes("horizontal")

    const pairCount = Math.ceil(targetLength / 2)

    for (let i = 0; i < pairCount; i++) {
      const image = i < images.length ? structuredClone(images[i]) : structuredClone(images[i % images.length])
      const text = i < texts.length ? structuredClone(texts[i]) : structuredClone(texts[i % texts.length])

      image.id = uuidv4()
      text.id = uuidv4()

      if (isHorizontal || !isRight) {
        newData.push(image, text)
      } else {
        newData.push(text, image)
      }
    }

    return newData.slice(0, targetLength)
  }
  public fillEmptyLayout(layout: Layouts[]): Layouts[] {
    var newLayout = layout
    newLayout.forEach((ele) => {
      if (ele.layouts === null || ele.layouts === undefined || ele.layouts?.length === 0) {
        if (ele.objType === "Booking_card") {
          ele.layouts = [{ priority: 0, objType: "Booking_card" }]
        }
        if (ele.objType === "Divider") {
          ele.layouts = [{ priority: 0, objType: "HDDivider" }]
        }
        if (ele.objType === "Image_1") {
          ele.layouts = [{ priority: 0, objType: "HDImage" }]
        }
        if (ele.objType === "Carousel") {
          ele.layouts = [{ priority: 0, objType: "HDImageWithTitle" }]
        }
      }
    })
    return newLayout
  }
  public cleanUpNewLineSpaces(layout: Layouts[]): Layouts[] {
    var newLayout = layout
    newLayout.forEach((ele) => {
      if (ele.layouts && (ele.objType === "Header" || ele.objType === "Subheader" || ele.objType === "Paragraph")) {
        var textObject = ele.layouts[0] as HDText
        for (const key in textObject.text) {
          if (textObject.text.hasOwnProperty(key)) {
            textObject.text[key] = textObject.text[key].replaceAll(/\n$/g, "")
          }
        }
        ele.layouts[0] = textObject
      }
      if (ele.objType.includes("Card") && ele.layouts) {
        ele.layouts.forEach((lay, index) => {
          if (lay.objType === "HDCardText") {
            var card = lay as HDCardText
            for (const key in card.header) {
              if (card.header.hasOwnProperty(key)) {
                card.header[key] = card.header[key].replaceAll(/\n$/g, "")
              }
            }
            for (const key in card.subHeader) {
              if (card.subHeader.hasOwnProperty(key)) {
                card.subHeader[key] = card.subHeader[key].replaceAll(/\n$/g, "")
              }
            }
            for (const key in card.paragraph) {
              if (card.paragraph.hasOwnProperty(key)) {
                card.paragraph[key] = card.paragraph[key].replaceAll(/\n$/g, "")
              }
            }
            ele.layouts[index] = card
          }
        })
      }
    })
    return newLayout
  }

  public convertPreviewElementToLayout(previewElements: HDComponent[]): Layouts[] {
    var layout = [] as Layouts[]
    previewElements.forEach((ele, position) => {
      ele.priority = position
      if (ele.backendName === "SoMe" || ele.category === "Card") {
        ele.data.forEach((ele, index) => {
          ele.priority = index
        })
      }
      if (ele.backendName === "HDSection") {
        var section = { objType: ele.backendName, priority: position, layouts: this.convertObjectToLayoutFormat(ele.data), condition: ele.condition, conditionText: ele.conditionText, conditionMode: ele.conditionMode, internalName: ele.internalName } as Layouts
        layout.push(section)
      } else {
        var section = { objType: ele.backendName, priority: position, layouts: ele.data, internalName: ele.internalName } as Layouts
        layout.push(section)
      }
    })
    return layout
  }

  //Function to hash and encode token
  public encodeToken(token: string): string {
    let hashedToken = sha256(token)
    hashedToken = encodeURIComponent(hashedToken)
    return hashedToken
  }
}
